import { useMutation, useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';

import { Alert } from '../../components/ui/Alert';
import { Avatar } from '../../components/ui/Avatar';
import { Button } from '../../components/ui/Button';
import { Explicit } from '../../components/ui/Explicit';
import { SkeletonRow } from '../../components/ui/Loader';
import { ApiError, api } from '../../lib/api';
import { useSession } from '../../stores/session';
import { Modal } from '../albums/Modal';
import { Artwork } from '../dashboard/Artwork';
import type { TrackSummary } from '../tracks/TrackList';
import { duration, rooms } from './api';

/**
 * An artist as the search returns them. Enough to draw a row and drill in.
 */
interface ArtistHit {
  id: number;
  username: string;
  display_name: string;
  avatar: string | null;
  follower_count: number;
}

/**
 * The crate (dev/design/GetVibed DJ Console.dc.html).
 *
 * ## A host plays other people's records
 *
 * That is the difference between this and `SubmitDialog`, which lists only your
 * own tracks and needs no search. A host reaching for something has a name in
 * their head, so this is a search box over the whole catalogue - the same
 * public endpoint the search page uses.
 *
 * ## The name in their head is often an artist, not a title
 *
 * So this searches both, and an artist result drills into their catalogue
 * rather than navigating anywhere. Two requests per search rather than one
 * `type=all`, which caps each side at six - a host searching a title wants
 * twenty results, and losing fourteen of them to make room for artists would
 * be a worse crate than the one that could not find artists at all.
 *
 * ## It opens on your own tracks
 *
 * The empty state used to be a sentence explaining that the catalogue was
 * searchable. A host's own catalogue is the one thing they will queue without
 * needing to look it up - the set opener, the thing they just released - and
 * making them type their own name for it was the wrong default.
 *
 * ## Two buttons, not one and a checkbox
 *
 * "Add" and "Play next" are different intentions and a host mid-broadcast
 * should not have to set a toggle and then press a button to express either.
 * Play next pins - a host pick sits above every weight, which is what makes it
 * the answer to "play this now".
 *
 * The row stays on screen after it is added, marked, rather than the dialog
 * closing. A host queuing a set adds four things in a row, and a modal that
 * shuts after each one would make that four searches.
 */
export function CrateDialog({ onClose, onAdded }: { onClose: () => void; onAdded: () => void }) {
  const me = useSession((s) => s.user);
  const [query, setQuery] = useState('');
  const [search, setSearch] = useState('');
  /** An artist whose catalogue is open, over the top of everything else. */
  const [viewing, setViewing] = useState<ArtistHit | null>(null);
  const [added, setAdded] = useState<Record<number, 'queued' | 'pinned'>>({});

  /*
   * Debounced, because this fires on every keystroke otherwise and the search
   * endpoint is rate limited at 300/hour - a host typing "nightbus" would spend
   * eight of them on prefixes nobody wanted results for.
   */
  useEffect(() => {
    const timer = setTimeout(() => setSearch(query.trim()), 250);

    return () => clearTimeout(timer);
  }, [query]);

  const searching = search !== '' && viewing === null;

  const tracksQuery = useQuery({
    queryKey: ['search', search, 'tracks'],
    queryFn: () => api.get<{ tracks: TrackSummary[] }>(`/search?q=${encodeURIComponent(search)}&type=tracks`),
    enabled: searching,
  });

  const artistsQuery = useQuery({
    queryKey: ['search', search, 'users'],
    queryFn: () => api.get<{ users: ArtistHit[] }>(`/search?q=${encodeURIComponent(search)}&type=users`),
    enabled: searching,
  });

  /*
   * Whoever's catalogue is on screen: the artist drilled into, or you.
   *
   * `enabled` covers both by being false while a search is showing, so
   * switching back from a drill-down does not refetch what is already cached.
   */
  const catalogueOf = viewing?.username ?? me?.username ?? '';
  const catalogue = useQuery({
    queryKey: ['tracks', catalogueOf],
    queryFn: () => api.get<{ items: TrackSummary[] }>(`/users/${catalogueOf}/tracks`),
    enabled: !searching && catalogueOf !== '',
  });

  const add = useMutation({
    mutationFn: ({ id, pin }: { id: number; pin: boolean }) => rooms.add(id, pin),
    onSuccess: (_result, { id, pin }) => {
      setAdded((current) => ({ ...current, [id]: pin ? 'pinned' : 'queued' }));
      onAdded();
    },
  });

  const tracks = tracksQuery.data?.tracks ?? [];
  const artists = artistsQuery.data?.users ?? [];

  /*
   * Only what a room can actually take.
   *
   * `PlayableTrack` requires ready, public, released and unheld. Search results
   * are already filtered that way by the query; a catalogue read is not, and it
   * is your own, so it comes back with drafts and private tracks in it.
   */
  const owned = (catalogue.data?.items ?? []).filter(
    (track) => track.status === 'ready' && track.visibility === 'public',
  );

  const row = (track: TrackSummary) => (
    <TrackRow key={track.id} track={track} state={added[track.id]} add={add} />
  );

  return (
    <Modal title="Add to your queue" onClose={onClose}>
      <div className="flex flex-col gap-4" data-crate>
        <input
          value={query}
          autoFocus
          data-crate-search
          placeholder="Search tracks and artists"
          onChange={(event) => {
            setQuery(event.target.value);
            // Typing is a new intention. Staying inside somebody's catalogue
            // while results the host cannot see accumulate behind it is the
            // kind of thing that reads as a broken search box.
            setViewing(null);
          }}
          className="rounded-[var(--radius-control)] border border-line bg-app px-3.5 py-2.5 text-sm text-ink placeholder:text-faint focus:border-line-strong"
        />

        {add.isError && (
          <Alert tone="error">
            {add.error instanceof ApiError ? add.error.message : 'That could not be added.'}
          </Alert>
        )}

        {viewing ? (
          /* ------------------------------------------------------ */
          /* One artist's catalogue                                  */
          /* ------------------------------------------------------ */
          <>
            <div className="flex items-center gap-2.5">
              <button
                type="button"
                data-crate-back
                onClick={() => setViewing(null)}
                aria-label="Back to results"
                className="flex h-8 w-8 shrink-0 items-center justify-center rounded-[var(--radius-pill)] border border-line text-soft transition-colors hover:border-line-strong hover:text-ink"
              >
                <svg viewBox="0 0 16 16" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="1.6" aria-hidden="true">
                  <path d="M10 3L5 8l5 5" />
                </svg>
              </button>
              <Avatar src={viewing.avatar} name={viewing.display_name} size={32} />
              <div className="flex min-w-0 flex-col">
                <span className="truncate text-sm font-semibold text-ink">
                  {viewing.display_name}
                </span>
                <span className="truncate font-mono text-[10px] text-faint">
                  @{viewing.username}
                </span>
              </div>
            </div>

            <Results
              busy={catalogue.isLoading}
              empty="Nothing published yet."
              items={owned}
              render={row}
            />
          </>
        ) : searching ? (
          /* ------------------------------------------------------ */
          /* Results                                                 */
          /* ------------------------------------------------------ */
          artistsQuery.isLoading || tracksQuery.isLoading ? (
            <Busy />
          ) : artists.length === 0 && tracks.length === 0 ? (
            <p className="py-6 text-center text-sm text-soft">Nothing found for “{search}”.</p>
          ) : (
            <div className="flex max-h-[24rem] flex-col gap-4 overflow-y-auto" data-crate-results>
              {artists.length > 0 && (
                <div className="flex flex-col gap-2">
                  <span className="meta-caps text-faint">artists</span>
                  {artists.map((artist) => (
                    <button
                      key={artist.id}
                      type="button"
                      data-crate-artist={artist.username}
                      onClick={() => setViewing(artist)}
                      className="flex items-center gap-3 rounded-[var(--radius-card)] border border-line p-2.5 text-left transition-colors hover:border-line-strong"
                    >
                      <Avatar src={artist.avatar} name={artist.display_name} size={40} />
                      <div className="flex min-w-0 flex-1 flex-col">
                        <span className="truncate text-sm font-medium text-ink">
                          {artist.display_name}
                        </span>
                        <span className="truncate font-mono text-[10px] text-faint">
                          @{artist.username} · {artist.follower_count.toLocaleString()} follower
                          {artist.follower_count === 1 ? '' : 's'}
                        </span>
                      </div>
                      <span className="shrink-0 font-mono text-[10px] text-ghost">
                        their tracks →
                      </span>
                    </button>
                  ))}
                </div>
              )}

              {tracks.length > 0 && (
                <div className="flex flex-col gap-2">
                  <span className="meta-caps text-faint">tracks</span>
                  {tracks.map(row)}
                </div>
              )}
            </div>
          )
        ) : (
          /* ------------------------------------------------------ */
          /* Your own, which is the default                          */
          /* ------------------------------------------------------ */
          <>
            <span className="meta-caps text-faint">your tracks</span>
            <Results
              busy={catalogue.isLoading}
              empty="You have not published anything yet. Search for something else to play."
              items={owned}
              render={row}
            />
          </>
        )}

        <div className="flex justify-end">
          <Button variant="ghost" onClick={onClose}>
            Done
          </Button>
        </div>
      </div>
    </Modal>
  );
}

function Busy() {
  return (
    <div className="flex flex-col gap-2" aria-busy="true">
      {[0, 1, 2].map((i) => (
        <SkeletonRow key={i} />
      ))}
    </div>
  );
}

/** A scrolling list, or the one sentence that stands in for it. */
function Results({
  busy,
  empty,
  items,
  render,
}: {
  busy: boolean;
  empty: string;
  items: TrackSummary[];
  render: (track: TrackSummary) => React.ReactNode;
}) {
  if (busy) return <Busy />;

  if (items.length === 0) {
    return <p className="py-6 text-center text-sm text-soft">{empty}</p>;
  }

  return (
    <ul className="flex max-h-[24rem] flex-col gap-2 overflow-y-auto" data-crate-results>
      {items.map(render)}
    </ul>
  );
}

function TrackRow({
  track,
  state,
  add,
}: {
  track: TrackSummary;
  state: 'queued' | 'pinned' | undefined;
  add: { isPending: boolean; mutate: (input: { id: number; pin: boolean }) => void };
}) {
  return (
    <li className="flex items-center gap-3 rounded-[var(--radius-card)] border border-line p-2.5">
      <Artwork
        track={{ title: track.title, artwork: null, artwork_small: track.artwork_small ?? null }}
        className="h-10 w-10 shrink-0 rounded-[var(--radius-chip)]"
      />

      <div className="flex min-w-0 flex-1 flex-col">
        <span className="truncate text-sm font-medium text-ink">
          {track.explicit && <Explicit className="mr-1.5 align-[-2px]" />}
          {track.title}
        </span>
        <span className="truncate font-mono text-[10px] text-faint">
          {track.owner?.display_name ?? 'unknown'} · {duration(track.duration_ms)}
        </span>
      </div>

      {state ? (
        // Marked rather than removed. A host who added the wrong one needs to
        // see that they did before they add it again.
        <span
          data-crate-added={track.id}
          className={`shrink-0 font-mono text-[10px] ${state === 'pinned' ? 'text-fresh' : 'text-soft'}`}
        >
          {state === 'pinned' ? 'playing next' : 'in the queue'}
        </span>
      ) : (
        <div className="flex shrink-0 items-center gap-1.5">
          <button
            type="button"
            data-crate-add={track.id}
            disabled={add.isPending}
            onClick={() => add.mutate({ id: track.id, pin: false })}
            className="rounded-[var(--radius-chip)] border border-line px-2.5 py-1.5 font-mono text-[10px] text-soft transition-colors hover:border-line-strong hover:text-ink"
          >
            add
          </button>
          <button
            type="button"
            title="Host pick — pins above every weight"
            data-crate-pin={track.id}
            disabled={add.isPending}
            onClick={() => add.mutate({ id: track.id, pin: true })}
            className="rounded-[var(--radius-chip)] border border-fresh-edge bg-fresh-wash px-2.5 py-1.5 font-mono text-[10px] text-fresh transition-opacity hover:opacity-80"
          >
            play next
          </button>
        </div>
      )}
    </li>
  );
}
